import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import { loginPharmacy } from '../../../redux/middleware/user/pharmacy/login';
import { pharmacyGoogleLogin } from '../../../redux/middleware/user/pharmacy/googleAuth';
import { pharmacyVerifyCaptcha } from '../../../redux/middleware/user/pharmacy/captcha';
import {
	openModal,
	closeModal,
} from '../../../redux/middleware/user/pharmacy/modal';
import ReCAPTCHA from 'react-google-recaptcha';
import { CircularProgress, Dialog } from '@mui/material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff } from 'react-feather';
import Divider from '../misc/Divider';
import { APP_URLS } from '../../../../urls';

const PharmacyLogin = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const siteKey = process.env.GOOGLE_RECAPTCH_SITE_KEY;
	const { loading, error, success, isVerified, isModalOpen, isAuthenticated } =
		useSelector((state) => state.pharmacyLogin);
	const client_id = process.env.GOOGLE_CLIENT_ID;
	const [formData, setFormData] = useState({
		email: '',
		password: '',
		recaptcha_token: '',
	});
	const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({
		email: '',
		password: '',
		captcha: '',
		general: '',
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });

		// Clear the field error when user starts typing
		if (fieldErrors[name]) {
			setFieldErrors({
				...fieldErrors,
				[name]: '',
			});
		}
	};

	const toggleShowPassword = () => setShowPassword((prev) => !prev);

	const handleRecaptchaChange = async (token) => {
		setFormData({ ...formData, recaptcha_token: token });

		try {
			await dispatch(pharmacyVerifyCaptcha(token));
			setIsCaptchaVerified(true);
			setFieldErrors({
				...fieldErrors,
				captcha: '',
			});
		} catch (error) {
			setIsCaptchaVerified(false);
			setFieldErrors({
				...fieldErrors,
				captcha: 'reCAPTCHA verification failed. Please try again.',
			});
			console.error('reCAPTCHA verification failed:', error);
		}
	};

	useEffect(() => {
		if (isAuthenticated) {
			console.log('Redirecting after authentication...');
			navigate('/app/pharmacy/dashboardpage', { replace: true });
		}
	}, [isAuthenticated, navigate]);

	useEffect(() => {
		// Handle API errors
		if (error) {
			if (error.includes('Invalid credentials')) {
				setFieldErrors((prev) => ({
					...prev,
					general:
						'The email or password you entered is incorrect. Please try again.',
				}));
			} else if (error.includes('email')) {
				setFieldErrors((prev) => ({ ...prev, email: error }));
			} else if (error.includes('password')) {
				setFieldErrors((prev) => ({ ...prev, password: error }));
			} else if (error.includes('captcha')) {
				setFieldErrors((prev) => ({ ...prev, captcha: error }));
			} else {
				setFieldErrors((prev) => ({
					...prev,
					general: error || 'Login failed. Please check your credentials.',
				}));
			}
		}
	}, [error]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Reset all field errors
		setFieldErrors({
			email: '',
			password: '',
			captcha: '',
			general: '',
		});

		// Validate form
		let hasErrors = false;
		const newFieldErrors = { ...fieldErrors };

		if (!formData.email) {
			newFieldErrors.email = 'Email is required';
			hasErrors = true;
		}

		if (!formData.password) {
			newFieldErrors.password = 'Password is required';
			hasErrors = true;
		}

		if (!isCaptchaVerified) {
			newFieldErrors.captcha =
				'Please complete the reCAPTCHA verification before logging in.';
			hasErrors = true;
		}

		if (hasErrors) {
			setFieldErrors(newFieldErrors);
			return;
		}

		try {
			dispatch(loginPharmacy(formData)).then((response) => {
				if (response && response.error) {
					const errorMessage =
						response.error.message || 'Login failed. Please try again.';
					setFieldErrors((prevErrors) => ({
						...prevErrors,
						general: errorMessage,
					}));
				}
			});
		} catch (error) {
			console.error('Login error caught:', error);
			setFieldErrors({
				...fieldErrors,
				general: error.message || 'Login failed. Please try again.',
			});
		}
	};

	const handlepharmacyGoogleLogin = (response) => {
		if (response.error) {
			console.error('Google login failed', response.error);
			setFieldErrors({
				...fieldErrors,
				general: 'Google login failed. Please try again.',
			});
			return;
		}

		const tokenId = response.tokenId; // Google OAuth token

		dispatch(pharmacyGoogleLogin(tokenId));
	};

	const handleCloseClick = () => {
		dispatch(closeModal());
	};

	return (
		<>
			<form onSubmit={handleSubmit} className={css(styles.form)}>
				{/* General error message - Add this at the top of your form */}
				{fieldErrors.general && (
					<motion.div
						className={css(styles.errorMessage, styles.generalError)}
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3 }}
					>
						{fieldErrors.general}
					</motion.div>
				)}

				<motion.div
					className={css(styles.inputGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.1 }}
				>
					<input
						type="email"
						name="email"
						placeholder="Email"
						value={formData.email}
						onChange={handleChange}
						className={css(
							styles.input,
							fieldErrors.email && styles.inputError,
						)}
						required
					/>
					{fieldErrors.email && (
						<div className={css(styles.errorMessage)}>{fieldErrors.email}</div>
					)}
				</motion.div>

				<motion.div
					className={css(styles.inputGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.2 }}
				>
					<div className={css(styles.passwordInputWrapper)}>
						<input
							type={showPassword ? 'text' : 'password'}
							name="password"
							placeholder="Password"
							value={formData.password}
							onChange={handleChange}
							className={css(
								styles.input,
								styles.passwordInput,
								fieldErrors.password && styles.inputError,
							)}
							required
						/>
						<button
							type="button"
							onClick={toggleShowPassword}
							className={css(styles.passwordToggle)}
						>
							{showPassword ? (
								<EyeOff className={css(styles.eyeIcon)} />
							) : (
								<Eye className={css(styles.eyeIcon)} />
							)}
						</button>
					</div>
					{fieldErrors.password && (
						<div className={css(styles.errorMessage)}>
							{fieldErrors.password}
						</div>
					)}
				</motion.div>

				<motion.div
					className={css(styles.captchaGroup)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.3 }}
				>
					<div className={css(styles.captcha)}>
						<ReCAPTCHA sitekey={siteKey} onChange={handleRecaptchaChange} />
					</div>
					{fieldErrors.captcha && (
						<div className={css(styles.errorMessage)}>
							{fieldErrors.captcha}
						</div>
					)}
				</motion.div>

				<motion.button
					type="submit"
					className={css(styles.button)}
					disabled={loading || !isCaptchaVerified}
					whileHover={loading ? {} : { scale: 1.05 }}
					whileTap={loading ? {} : { scale: 0.95 }}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.4 }}
				>
					{loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
				</motion.button>

				<Divider />

				<motion.div
					whileHover={{ scale: 1.05 }}
					whileTap={{ scale: 0.95 }}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.5 }}
				>
					<GoogleOAuthProvider clientId={client_id}>
						<GoogleLogin
							logo_alignment="center"
							onSuccess={handlepharmacyGoogleLogin}
							onError={(error) => {
								console.error('Google Login Error:', error);
								setFieldErrors({
									...fieldErrors,
									general: 'Google login failed. Please try again.',
								});
							}}
						/>
					</GoogleOAuthProvider>
				</motion.div>

				<div className={css(styles.linkContainer)}>
					<motion.button
						type="button"
						onClick={() => navigate(APP_URLS.pharmacyForgotPassword)}
						className={css(styles.forgotPasswordText)}
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3, delay: 0.7 }}
					>
						Forgot Password?
					</motion.button>
				</div>
			</form>
		</>
	);
};

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '100vh',
		background:
			'linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url(https://i.pinimg.com/1200x/ca/94/db/ca94db6932da7a180866b708a3b4d25c.jpg)',
		backgroundSize: 'cover',
		backgroundPosition: 'center',
	},
	formContainer: {
		maxWidth: '500px',
		width: '100%',
		padding: '2rem',
		borderRadius: '8px',
		boxShadow: '0 4px 10px rgba(0, 0, 0, 0.25)',
		backgroundColor: 'rgba(255, 255, 255, 0.9)',
		backdropFilter: 'blur(10px)',
	},
	title: {
		fontSize: '2rem',
		fontWeight: '600',
		textAlign: 'center',
		marginBottom: '1.5rem',
		color: '#13334c',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
	},
	inputGroup: {
		position: 'relative',
		marginBottom: '1rem',
	},
	input: {
		width: '100%',
		padding: '0.75rem',
		borderRadius: '8px',
		border: '1px solid #e2e8f0',
		fontSize: '1rem',
		transition: 'border-color 0.3s',
		':focus': {
			outline: 'none',
			borderColor: '#2783F7',
			boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)',
		},
	},
	inputError: {
		borderColor: '#e53e3e',
		':focus': {
			boxShadow: '0 0 0 3px rgba(229, 62, 62, 0.1)',
		},
	},
	errorMessage: {
		color: '#e53e3e',
		fontSize: '0.875rem',
		marginTop: '0.25rem',
		marginBottom: '0.25rem',
	},
	generalError: {
		backgroundColor: '#fef2f2',
		borderRadius: '4px',
		padding: '0.75rem',
		borderLeft: '4px solid #e53e3e',
		marginBottom: '1rem',
	},
	captchaGroup: {
		marginBottom: '1rem',
	},
	passwordInputWrapper: {
		position: 'relative',
		width: '100%',
	},
	passwordInput: {
		paddingRight: '40px',
	},
	passwordToggle: {
		position: 'absolute',
		right: '10px',
		top: '50%',
		transform: 'translateY(-50%)',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: '#4a5568',
	},
	eyeIcon: {
		color: 'black',
		width: '1.15rem',
		height: '1.15rem',
	},
	captcha: {
		display: 'flex',
		justifyContent: 'start',
	},
	button: {
		backgroundColor: '#2783F7',
		color: 'white',
		padding: '0.75rem',
		borderRadius: '8px',
		border: 'none',
		fontSize: '1rem',
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'background-color 0.3s',
		':hover': {
			backgroundColor: '#1c5fa8',
		},
		':disabled': {
			backgroundColor: '#a0aec0',
			cursor: 'not-allowed',
		},
	},
	googleContainer: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		marginTop: '0.5rem',
		marginBottom: '0.5rem',
	},
	linkContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '0.75rem',
		marginTop: '1rem',
	},
	registerLink: {
		color: '#2783F7',
		textDecoration: 'none',
		fontSize: '0.875rem',
		fontWeight: '500',
		':hover': {
			textDecoration: 'underline',
		},
	},
	forgotPasswordText: {
		all: 'unset',
		color: '#2783F7',
		textAlign: 'center',
		fontSize: '0.875rem',
		fontWeight: '600',
		cursor: 'pointer',
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	dialogContent: {
		padding: '1.5rem',
		minWidth: '300px',
	},
	dialogTitle: {
		fontSize: '1.5rem',
		fontWeight: '600',
		marginBottom: '1.25rem',
		color: '#13334c',
	},
});

export default PharmacyLogin;
